import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { Bot } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { pilotMiniChat } from '@/constants/product'

type Phase = 'idle' | 'question' | 'thinking' | 'typing' | 'done'

interface PilotMiniChatProps {
    className?: string
}

/** A tiny exchange with Pilot that types itself out, pauses, then plays again. */
export const PilotMiniChat: FC<PilotMiniChatProps> = ({ className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.35 })
    const reduced = useReducedMotion()
    const [phase, setPhase] = useState<Phase>('idle')
    const [characters, setCharacters] = useState(0)
    const { question, answer } = pilotMiniChat

    useEffect(() => {
        if (reduced) {
            setPhase('done')
            setCharacters(answer.length)
            return
        }

        if (!inView) return

        let timer = 0

        if (phase === 'idle') timer = window.setTimeout(() => setPhase('question'), 500)
        if (phase === 'question') timer = window.setTimeout(() => setPhase('thinking'), 900)
        if (phase === 'thinking') timer = window.setTimeout(() => setPhase('typing'), 1300)

        if (phase === 'typing') {
            timer = window.setTimeout(() => {
                if (characters >= answer.length) setPhase('done')
                else setCharacters(count => Math.min(answer.length, count + 2))
            }, 28)
        }

        if (phase === 'done') {
            timer = window.setTimeout(() => {
                setCharacters(0)
                setPhase('idle')
            }, 5200)
        }

        return () => window.clearTimeout(timer)
    }, [phase, characters, inView, reduced, answer.length])

    const showQuestion = phase !== 'idle'
    const showAnswer = phase === 'typing' || phase === 'done'

    return (
        <div ref={ref} className={cn('flex min-h-[188px] flex-col justify-end gap-2.5', className)}>
            <span className='sr-only'>
                {`Example: "${question}" Pilot answers: "${answer}"`}
            </span>

            <div aria-hidden='true' className='flex flex-col gap-2.5'>
                <div className={cn('flex justify-end transition-all duration-500 ease-calm', showQuestion ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0')}>
                    <p className='max-w-[85%] rounded-[18px] bg-ink px-4 py-2.5 text-[13.5px] leading-snug text-white'>
                        {question}
                    </p>
                </div>

                <div className={cn('flex items-end gap-2 transition-all duration-500 ease-calm', phase === 'thinking' || showAnswer ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0')}>
                    <span className='grid size-7 shrink-0 place-items-center rounded-full bg-ultramarine text-white'>
                        <Bot className='size-3.5' strokeWidth={2} />
                    </span>

                    <div className='max-w-[85%] rounded-[18px] border border-ink/10 bg-paper px-4 py-2.5 text-[13.5px] leading-snug text-ink'>
                        {phase === 'thinking' && (
                            <span className='flex h-[18px] items-center gap-1'>
                                {[0, 1, 2].map(dot => (
                                    <span
                                        key={dot}
                                        className='size-1.5 rounded-full bg-ink/60 animate-typing-dot'
                                        style={{ animationDelay: `${dot * 160}ms` }}
                                    />
                                ))}
                            </span>
                        )}

                        {showAnswer && (
                            <span>
                                {answer.slice(0, characters)}
                                {phase === 'typing' && (
                                    <span className='ml-0.5 inline-block h-3.5 w-[2px] translate-y-0.5 bg-ultramarine animate-caret' />
                                )}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
